import React, { useEffect, useMemo, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { usePlacesWidget } from 'react-google-autocomplete'
import { GOOGLE_MAPS_API_KEY, INITIAL_MAP_COORDS } from '@next-nx/utils/settings'
import { useTrans, useTealium } from '@next-nx/hooks'
import { getTimestamp } from '@next-nx/utils'
import { MenuItemType, StoreLocatorItemType } from '@next-nx/utils/types'
import {
  ArrowRight,
  CarrozzerieIllustration,
  CentriCristalliIllustration,
  CentriDiConsegnaIllustration,
  GommistiIllustration,
  NavigationCancel,
  NavLocation,
  OfficineIllustration,
} from '@next-nx/shared-ui/icons'
import {
  Button,
  FormField,
  FormInput,
  FormSelect,
  Loader,
  Option,
  StorelocatorMarker,
  WContainer,
} from '@next-nx/shared-ui/atoms'
import { StoreLocatorResults, BoxLink } from '@next-nx/shared-ui/molecules'
import { StorelocatorMap } from '@next-nx/shared-ui/organisms'
import styles from './StoreLocatorForm.module.sass'

interface Props {
  type: string
  ctaLabel?: string
  resultCtaLabel?: string
  url?: string
  options?: any[]
  infoBox?: MenuItemType
  initialItems?: StoreLocatorItemType[]
  sharedInputPlace?:
    | {
        address: string
        coords: any
      }
    | undefined
  setSharedInputPlace?: any
  optionItems?: object
  loadingOptionItems?: object
  initialOption?: string
}

const StoreLocatorForm = (props: Props) => {
  const {
    type = '',
    ctaLabel = '',
    resultCtaLabel = '',
    url = undefined,
    options = [],
    infoBox = undefined,
    initialItems = [],
    sharedInputPlace = undefined,
    setSharedInputPlace = () => {},
    optionItems = {},
    loadingOptionItems = {},
    initialOption = undefined,
  } = props

  const t = useTrans()

  const distanzaMinima = 30
  const distanzaMinima2 = 20

  const initialSelectedOptionNome = initialOption
    ? initialOption
    : options[0]?.nomeStatico || undefined
  const [items, setItems] = useState(initialItems || optionItems?.[initialSelectedOptionNome] || [])
  const [selectedOption, setSelectedOption] = useState(
    initialSelectedOptionNome
      ? options.find((o) => o.nomeStatico == initialSelectedOptionNome)
      : null
  )
  const [selectedMarca, setSelectedMarca] = useState<{ pk: number } | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [inputPlace, setInputPlace] = useState<
    | {
        address: string
        coords: any
      }
    | undefined
  >(sharedInputPlace)
  const [results, setResults] = useState<StoreLocatorItemType[]>([])
  const [mapCoords, setMapCoords] = useState(sharedInputPlace?.coords || INITIAL_MAP_COORDS)
  const [showResults, setShowResults] = useState(false) // Inizialmente non c'è il box con i risultati, si visualizza dopo la prima ricerca
  const [showMarkers, setShowMarkers] = useState(true)
  const [selectedItem, setSelectedItem] = useState<number | null>(null)
  const [goToItem, setGoToItem] = useState<number | null>(null)
  const [geolocationAvailable, setGeolocationAvailable] = useState(false)
  const loadingSelectedOptionItems = loadingOptionItems?.[selectedOption?.nomeStatico]

  const { linkDataLayer } = useTealium()

  const mapOptionsIcons = {
    centro_carrozzeria: <CarrozzerieIllustration />,
    centro_gommista: <GommistiIllustration />,
    centro_officina: <OfficineIllustration />,
    centro_di_consegna: <CentriDiConsegnaIllustration />,
    centro_cristalli: <CentriCristalliIllustration />,
  }

  const filterResults = (coords: { lat: number; lng: number }, showAll = false) => {
    setMapCoords(coords)
    if (!showAll) {
      const prefilteredItems = selectedMarca?.pk
        ? items.filter((x) =>
            x.marche?.edges.map((x) => x.node).some((y) => y.pk === selectedMarca.pk)
          )
        : items
      let markers = calculateDistances(coords.lat, coords.lng, prefilteredItems)
      let markersFiltered = markers.filter(
        (c) =>
          c.distance <
          (selectedOption?.nomeStatico === 'centro_gommista' ||
          selectedOption?.nomeStatico === 'centro_officina'
            ? distanzaMinima2
            : distanzaMinima)
      )
      if (!markersFiltered.length) {
        const closest = nearest(markers)
        if (closest) markersFiltered.push(closest)
      }
      markers = markersFiltered

      setResults(markers)
      setShowResults(true)
    } else {
      if (type === 'agenzie' || type === 'filiali') {
        const markers = calculateDistances(coords.lat, coords.lng, items)
        setResults(markers)
      }
    }
  }

  const { ref: autocompleteRef } = usePlacesWidget({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelected: (place) => {
      setInputPlace({
        address: place.formatted_address || '',
        coords: {
          lat: place?.geometry?.location?.lat(),
          lng: place?.geometry?.location?.lng(),
        },
      })
    },
    options: {
      types: ['(regions)'],
      componentRestrictions: { country: 'it' },
    },
  })

  const geolocate = () => {
    navigator.geolocation.getCurrentPosition(function (position) {
      setInputPlace({
        address: '',
        coords: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
      })
    })
  }

  const handleTracking = (research_type?: string, action_type?: string, interazione?: number) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}
    let name =
      type === 'centri_convenzionati'
        ? 'strutture_convenzionate'
        : type === 'agenzie'
        ? type
        : 'filiali'

    switch (interazione) {
      case 1:
        dlObj = {
          virtual: 'action',
          action_name: 'storelocator',
          action_tech: 'click',
          action_type: `${name}_${action_type?.replace(/ /g, '_').toLowerCase()}`,
          action_timestamp,
          action_effect: `${name}_interazione_generale`,
        }
        break
      case 2:
        dlObj = {
          virtual: 'action',
          action_name: 'storelocator',
          action_tech: 'click',
          action_type: `${name}_filtri`,
          action_timestamp,
          action_effect: `${name}_filtri`,
          research_type: research_type?.replace(/ /g, '_').replace(/,/g, '').toLowerCase(),
        }
        break
      default:
        dlObj = {
          virtual: 'action',
          action_name: 'storelocator',
          action_tech: 'click',
          action_type: `${name}_ricerca`,
          action_timestamp,
          action_effect: `${name}_ricerca`,
          research_type: research_type?.replace(/ /g, '_').replace(/,/g, '').toLowerCase(),
        }
        break
    }
    linkDataLayer(dlObj)
  }

  useEffect(() => {
    if (inputPlace && !loadingSelectedOptionItems) {
      setInputValue(inputPlace.address)
      filterResults(inputPlace.coords)
      setSharedInputPlace(inputPlace)
      inputPlace.address && handleTracking(inputPlace.address)
    }
  }, [inputPlace, loadingSelectedOptionItems])

  useEffect(() => {
    if (selectedMarca?.pk && !loadingSelectedOptionItems) {
      filterResults(mapCoords, inputPlace?.coords ? false : true)
    }
  }, [selectedMarca, loadingSelectedOptionItems])

  useEffect(() => {
    if (selectedOption?.pk) {
      if (!loadingSelectedOptionItems) {
        setSelectedMarca(null)
        type === 'centri_convenzionati' &&
          setItems(optionItems?.[selectedOption?.nomeStatico] || [])
      } else {
        setItems([])
      }
    }
  }, [selectedOption, loadingSelectedOptionItems])

  useEffect(() => {
    filterResults(mapCoords, inputPlace?.coords ? false : true)
  }, [items])

  useEffect(() => {
    if (optionItems?.[selectedOption?.nomeStatico]) {
      setItems(optionItems?.[selectedOption?.nomeStatico])
    }
  }, [Object.keys(optionItems).length])

  const rad = (x: number) => (x * Math.PI) / 180

  const calculateDistances = (lat: number, lng: number, markers: any[]) => {
    var R = 6371
    let returnMarkers = []

    for (let marker of markers) {
      var mlat = marker.latitudine
      var mlng = marker.longitudine
      var dLat = rad(mlat - lat)
      var dLong = rad(mlng - lng)
      var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(rad(lat)) * Math.cos(rad(lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2)
      var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      var d = R * c
      returnMarkers.push({ ...marker, distance: d })
    }

    return returnMarkers
  }

  const nearest = (markers: any[]) => {
    var closest = -1

    for (let i = 0; i < markers.length; i++) {
      const d = markers[i].distance
      if (closest === -1 || d < markers[closest].distance) {
        closest = i
      }
    }

    return markers[closest]
  }

  const renderMarkers = () => {
    if (results.length > 0 && showMarkers)
      return results.map((item) => (
        <StorelocatorMarker
          key={`${selectedOption?.nomeStatico || type}-${item.pk}`}
          type={selectedOption?.nomeStatico || type}
          item={item}
          selected={selectedItem === item.pk}
          setSelected={(v: boolean) => {
            setSelectedItem(v && item?.pk ? item.pk : null)
            setGoToItem(v && item?.pk ? item.pk : null) // Quando si seleziona il marker si scrolla anche la lista dei risultati
          }}
          ctaLabel={resultCtaLabel}
        />
      ))
    else return null
  }

  const markers = useMemo(
    () => renderMarkers(),
    [results, selectedItem, showMarkers, type, selectedOption]
  )

  useEffect(() => {
    setGeolocationAvailable(navigator && 'geolocation' in navigator)
  }, [])

  const getMarcheOptions = () => {
    const generico = selectedOption?.marcaTipoCentro.filter((value) => value.marca === 'Generico')
    let opts = selectedOption?.marcaTipoCentro.map((x) => ({ label: x.marca, value: x.pk }))
    if (generico.length > 0) {
      opts = opts.filter((value) => value.label !== generico[0].marca)
      opts.unshift({ label: generico[0].marca, value: generico[0].pk })
    }
    return opts
  }

  return (
    <div className={styles.root}>
      <WContainer>
        <div className={styles.form}>
          <div className={styles.inputWrap}>
            <FormInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('Inserisci il luogo, città o CAP')}
              ref={autocompleteRef}
            />
            {!!inputValue && (
              <button className={styles.inputReset} onClick={() => setInputValue('')}>
                <NavigationCancel />
              </button>
            )}
          </div>
          {geolocationAvailable && (
            <>
              <span className={styles.or}>{t('Oppure')}</span>
              <Button
                label={t('Utilizza la tua posizione')}
                icon={<NavLocation />}
                variant="ghost"
                onClick={() => {
                  geolocate()
                  handleTracking('utilizza_la_tua_posizione')
                }}
              />
            </>
          )}
        </div>
        {options.length > 0 && (
          <div className={styles.options}>
            <Swiper
              spaceBetween={8}
              slidesPerView={3.1}
              breakpoints={{
                576: {
                  spaceBetween: 16,
                  slidesPerView: 2.8,
                },
                768: {
                  spaceBetween: 16,
                  slidesPerView: 3.5,
                },
                992: {
                  spaceBetween: 16,
                  slidesPerView: 5,
                },
              }}
            >
              {options.map((x) => (
                <SwiperSlide key={x.pk}>
                  <Option
                    label={x.nome}
                    icon={mapOptionsIcons[x.nomeStatico]}
                    active={x.pk === selectedOption.pk}
                    onClick={() => {
                      setSelectedOption(x), handleTracking(x.nome, '', 2)
                    }}
                    className={styles.option}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
        {!!selectedOption?.marcaTipoCentro?.length && (
          <div className={styles.marcheBox}>
            <FormField label={t('Seleziona una marca')} layout="full">
              <FormSelect
                key={`marca-${selectedOption?.pk}`}
                placeholder={t('Tutte le marche')}
                value={selectedMarca?.pk}
                options={getMarcheOptions()}
                onChange={(e) => {
                  setSelectedMarca({ pk: e.target.value }),
                    handleTracking(
                      selectedOption?.marcaTipoCentro?.find((x) => x.pk === e.target.value)?.marca,
                      '',
                      2
                    )
                }}
                className={styles.marcheDropdown}
              />
            </FormField>
          </div>
        )}
      </WContainer>
      <div className={styles.mapWrap}>
        {loadingSelectedOptionItems && (
          <>
            <div className={styles.overlay} />
            <div className={styles.loadingModal}>
              <Loader className={styles.itemsLoader} />
            </div>
          </>
        )}
        <div className={styles.map}>
          <StorelocatorMap
            loaderClassName={styles.loader}
            mapContainerClassName={styles.mapContainer}
            items={results}
            setSelectedItem={setSelectedItem}
            showMarkers={true}
            mapCoords={mapCoords}
            key={type}
          >
            {markers}
          </StorelocatorMap>
        </div>
        {!!infoBox && (
          <WContainer>
            <BoxLink item={infoBox} ctaVariant="primary" className={styles.infoBox} size="sm" />
          </WContainer>
        )}
        {showResults && (
          <StoreLocatorResults
            type={type}
            option={selectedOption}
            results={results}
            ctaLabel={resultCtaLabel}
            className={styles.results}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            goToItem={goToItem}
            setGoToItem={setGoToItem}
          />
        )}
      </div>
      {!!ctaLabel && !!url && (
        <div className={styles.ctaBox}>
          <WContainer>
            <Button
              label={ctaLabel}
              href={url}
              variant="ghost"
              icon={<ArrowRight />}
              iconPos="right"
              onClick={() => handleTracking('', ctaLabel, 1)}
            />
          </WContainer>
        </div>
      )}
    </div>
  )
}

export default StoreLocatorForm
